import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  UploadCloud,
  Upload,
  Search,
  ShoppingCart,
  Phone,
  Play,
  Sheet,
  Check,
} from "lucide-react";
import PageWrapper from "components/PageWrapper";
import { uploadBomFileAPI } from "actions/bomActions";

const STEPS = [
  { n: 1, state: "done", title: "Connect a distributor", desc: "At least one API key is configured.", tag: "Done" },
  { n: 2, state: "current", title: "Upload your first BOM", desc: "We'll match each part across 40+ distributors.", tag: "You're here" },
  { n: 3, state: "upcoming", title: "Compare & place orders", desc: "Pick the best price and route POs automatically.", tag: "Next" },
];

const focusHeaderSearch = (e) => {
  if (e) e.preventDefault();
  document.getElementById("global-search")?.focus();
};

const GetStartedPage = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state?.user?.user);
  const firstName = (user?.name || "").trim().split(/\s+/)[0] || "there";

  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  const handleUpload = async (file) => {
    if (!file) return;
    const response = await uploadBomFileAPI(file);
    if (response.status) navigate(`/bom-column-mapping/${response.data?.unique_id}`);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer?.files?.length) handleUpload(e.dataTransfer.files);
  };

  return (
    <PageWrapper>
      <div className="flex flex-col gap-6">
        {/* Page head */}
        <div className="flex flex-col gap-1.5">
          <h1 className="text-[28px] leading-[34px] font-bold tracking-[-0.015em] text-s360-fg1">
            Welcome to semi360, {firstName} 👋
          </h1>
          <span className="text-sm text-s360-fg3">
            You're all set up. Upload your first BOM to start benchmarking prices across 40+
            distributors.
          </span>
        </div>

        {/* Hero dropzone */}
        <div
          className={`bg-white rounded-lg shadow-sm border-2 border-dashed flex flex-col items-center text-center gap-2.5 px-7 py-10 cursor-pointer transition-colors ${
            dragging ? "border-s360-violet bg-s360-violet-50/40" : "border-s360-stroke2 hover:border-s360-violet-100"
          }`}
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          role="button"
          tabIndex={0}
        >
          <span className="w-14 h-14 rounded-2xl bg-s360-violet-50 text-s360-violet inline-flex items-center justify-center mb-1">
            <UploadCloud className="w-7 h-7" />
          </span>
          <h2 className="text-lg font-semibold text-s360-fg1">Upload your first BOM</h2>
          <p className="text-sm text-s360-fg3 max-w-[460px]">
            Drag &amp; drop your BOM file here, or click to browse. Supports <strong>.xlsx</strong>,{" "}
            <strong>.xls</strong> and <strong>.csv</strong> up to 10&nbsp;MB.
          </p>
          <div className="flex gap-2.5 mt-2 flex-wrap justify-center">
            <button
              onClick={(e) => {
                e.stopPropagation();
                fileRef.current?.click();
              }}
              className="inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-s360-violet text-white text-sm font-semibold hover:bg-s360-violet-600"
            >
              <Upload className="w-4 h-4" /> Upload BOM
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                fileRef.current?.click();
              }}
              className="inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-white border border-s360-stroke1 text-sm font-semibold text-s360-fg1 hover:bg-s360-bg2"
            >
              <Sheet className="w-4 h-4" /> Use a sample BOM
            </button>
          </div>
          <span className="text-[13px] text-s360-fg3 mt-1" onClick={(e) => e.stopPropagation()}>
            or{" "}
            <a href="#" onClick={focusHeaderSearch} className="text-s360-violet font-semibold hover:underline">
              search parts manually →
            </a>
          </span>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={(e) => e.target.files?.length && handleUpload(e.target.files)}
          />
        </div>

        {/* 3-step guide */}
        <section className="flex flex-col gap-2">
          <h3 className="text-base font-semibold text-s360-fg1">Get started in 3 steps</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {STEPS.map((s) => {
              const tone =
                s.state === "done"
                  ? { num: "bg-s360-teal text-s360-ink", tag: "bg-s360-teal-50 text-[#0B7D72]", ring: "" }
                  : s.state === "current"
                    ? {
                        num: "bg-s360-violet text-white",
                        tag: "bg-s360-violet-50 text-s360-violet",
                        ring: "border-s360-violet-100 shadow-md",
                      }
                    : { num: "bg-s360-bg2 text-s360-fg3", tag: "bg-s360-bg2 text-s360-fg3", ring: "" };
              return (
                <div
                  key={s.n}
                  className={`bg-white rounded-lg shadow-sm border p-[18px] flex flex-col gap-2 ${
                    s.state === "current" ? tone.ring : "border-transparent"
                  }`}
                >
                  <span className={`w-7 h-7 rounded-full inline-flex items-center justify-center text-[13px] font-bold ${tone.num}`}>
                    {s.state === "done" ? <Check className="w-4 h-4" /> : s.n}
                  </span>
                  <span className="text-sm font-semibold text-s360-fg1">{s.title}</span>
                  <span className="text-[13px] text-s360-fg3">{s.desc}</span>
                  <span
                    className={`mt-1 self-start text-[11px] font-semibold uppercase tracking-[0.08em] px-2 py-0.5 rounded-full ${tone.tag}`}
                  >
                    {s.tag}
                  </span>
                </div>
              );
            })}
          </div>
        </section>

        {/* Quick links */}
        <section className="flex flex-col gap-2">
          <h3 className="text-base font-semibold text-s360-fg1">Or explore</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button
              onClick={focusHeaderSearch}
              className="bg-white rounded-md shadow-sm p-[18px] flex flex-col gap-3 text-left border border-transparent hover:shadow-md hover:border-s360-violet-100 transition-all"
            >
              <span className="w-9 h-9 rounded-lg bg-s360-teal-50 text-s360-teal inline-flex items-center justify-center">
                <Search className="w-5 h-5" />
              </span>
              <span className="text-sm font-semibold text-s360-fg1">Search parts</span>
              <span className="text-xs text-s360-fg3">Type an MPN in the search bar up top</span>
            </button>
            <button
              onClick={() => navigate("/suppliers")}
              className="bg-white rounded-md shadow-sm p-[18px] flex flex-col gap-3 text-left border border-transparent hover:shadow-md hover:border-s360-violet-100 transition-all"
            >
              <span className="w-9 h-9 rounded-lg bg-s360-violet-50 text-s360-violet inline-flex items-center justify-center">
                <Phone className="w-5 h-5" />
              </span>
              <span className="text-sm font-semibold text-s360-fg1">Manage suppliers</span>
              <span className="text-xs text-s360-fg3">Add API keys, set preferred sources</span>
            </button>
            <button
              onClick={() => navigate("/orders")}
              className="bg-white rounded-md shadow-sm p-[18px] flex flex-col gap-3 text-left border border-transparent hover:shadow-md hover:border-s360-violet-100 transition-all"
            >
              <span className="w-9 h-9 rounded-lg bg-s360-warning-bg text-s360-warning inline-flex items-center justify-center">
                <ShoppingCart className="w-5 h-5" />
              </span>
              <span className="text-sm font-semibold text-s360-fg1">View orders</span>
              <span className="text-xs text-s360-fg3">Track POs once you place them</span>
            </button>
            <button className="bg-white rounded-md shadow-sm p-[18px] flex flex-col gap-3 text-left border border-transparent hover:shadow-md hover:border-s360-violet-100 transition-all">
              <span className="w-9 h-9 rounded-lg bg-s360-success-bg text-s360-success inline-flex items-center justify-center">
                <Play className="w-5 h-5" />
              </span>
              <span className="text-sm font-semibold text-s360-fg1">Watch a 2-min demo</span>
              <span className="text-xs text-s360-fg3">See the full BOM-to-order flow</span>
            </button>
          </div>
        </section>
      </div>
    </PageWrapper>
  );
};

export default GetStartedPage;
